import { pipe, flatten, join, values } from 'ramda';
import { SELL_FLOWS } from '~/config/constants';
import { SalesMethodTypes } from '~/App/shared/interfaces/store/SellFlow';
import { TranslateFunction } from '~/Locale';
import { AxiosError } from 'axios';

interface DoneRouteForBusinessProposalProps {
  salesFlow: keyof typeof SELL_FLOWS | '';
  isManualValuationRequired: boolean;
  isTradeIn: boolean;
  salesMethod?: SalesMethodTypes | '';
  baseObjectId: string;
  token?: string;
}

export const getDoneRouteForBusinessProposal = ({
  salesFlow,
  isManualValuationRequired,
  isTradeIn,
  salesMethod = SalesMethodTypes.BIDDING,
  baseObjectId,
  token = 'invalid_token' // for tradeIn token is not sent
}: DoneRouteForBusinessProposalProps): string => {
  if (isTradeIn || isManualValuationRequired) {
    return '/ROUTES.SELL/ROUTES.THANK_YOU/ROUTES.THANK_YOU_MANUAL';
  }

  if (salesMethod === SalesMethodTypes.FIXED_PRICE) {
    return '/ROUTES.SELL/ROUTES.THANK_YOU/ROUTES.THANK_YOU_EXPRESS';
  }

  switch (salesFlow) {
    case SELL_FLOWS.CONSUMER_CARS:
    case SELL_FLOWS.CORPORATE_CARS_SMALL:
      return `/ROUTES.SELL/ROUTES.SELL_RESERVATION_PRICE/${baseObjectId}/${token}`;
    case SELL_FLOWS.CORPORATE_CARS:
      return '/ROUTES.SELL/ROUTES.SELLFLOW_CONFIRM_OR_CONTINUE_COMPANY';
    case SELL_FLOWS.NOTICE_OF_INTEREST:
    default:
      return '/ROUTES.SELL/ROUTES.SELLFLOW_CONFIRM_OR_CONTINUE_COMPANY';
  }
};

export interface BusinessProposalResponse {
  baseObjectId: string;
  clientHasMemberAccount: boolean;
  clientMemberEmail: string;
  clientMemberId: string;
}

export interface ErrorResponse {
  response?: {
    status: number;
    data?: {
      message:
        | {
            [s: string]: string | string[] | { [s: string]: string | string[] };
          }
        | string;
    };
  };
}

export const getProposalErrorMessage = (
  error: ErrorResponse,
  t: TranslateFunction
): string => {
  const errorCode = error?.response?.status;
  const errorMessage = error?.response?.data?.message;
  const defaultError = t('Something went wrong, please try again later.');

  if (errorCode === 404) {
    return t('Could not find a vehicle with this registration number');
  }

  if (typeof errorMessage === 'string') {
    return errorMessage;
  }

  if (errorMessage) {
    return pipe(values, flatten, join(' '))(errorMessage);
  }

  return defaultError;
};

export const getShouldUseFallbackSolution = (error: AxiosError) => {
  const errorCode = error?.response?.status;
  if (errorCode === 400 || errorCode === 404) {
    return false;
  }
  return true;
};
